import { useState } from 'react'
import { Login, useLogin, useNotify } from 'react-admin'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import CardActions from '@material-ui/core/CardActions'
import Typography from '@material-ui/core/Typography'

const useStyles = makeStyles((theme) => ({
  form: {
    padding: '0 1em 1em 1em'
  },
  input: {
    marginTop: '1em'
  },
  button: {
    width: '100%',
    marginTop: '1em'
  },
  error: {
    color: theme.palette.error.main,
    marginTop: '0.5em'
  },
  info: {
    marginTop: '1em',
    color: theme.palette.text.secondary,
    fontSize: '0.85em'
  }
}))

const errorMessages: Record<string, string> = {
  NO_CODE_EXISTS: 'Kaksivaiheisen tunnistautumisen koodia ei löydetty!',
  TOO_MANY_ATTEMPTS_CODE: 'Liian monta yritystä. Koita kirjautua uudelleen.',
  EXPIRED_CODE: 'Kaksivaiheisen tunnistautumisen koodi on vanhentunut.',
  INVALID_CODE: 'Koodi on väärin!'
}

function CustomLoginForm() {
  const classes = useStyles()
  const login = useLogin()
  const notify = useNotify()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login({ username, password })
    } catch (err: any) {
      if (err?.status === 'code_sent') {
        setTwoFactorRequired(true)
      } else {
        notify('Käyttäjänimi tai salasana oli virheellinen.', 'warning')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login({ username, password, code })
    } catch (err: any) {
      const message =
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        err?.body?.message
      if (message) {
        try {
          const parsed = JSON.parse(message)
          if (parsed.errorType === 'INVALID_CODE') {
            setError(`${errorMessages[parsed.errorType]} ${parsed.attemptsLeft} yritystä jäljellä`)
          } else {
            setError(errorMessages[parsed.errorType] || 'Kirjautuminen epäonnistui.')
          }
        } catch {
          setError('Kirjautuminen epäonnistui.')
        }
      } else {
        setError('Kirjautuminen epäonnistui.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (twoFactorRequired) {
    return (
      <form onSubmit={handleConfirmCode}>
        <div className={classes.form}>
          <Typography className={classes.info}>
            Kaksivaiheisen tunnistamisen kertakäyttökoodi on lähetetty
            sähköpostiisi. Koodi saapuu muutaman minuutin sisällä.
          </Typography>
          <div className={classes.input}>
            <TextField
              label="Vahvistuskoodi"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              autoFocus
              disabled={loading}
            />
          </div>
          {error && (
            <Typography className={classes.error}>{error}</Typography>
          )}
        </div>
        <CardActions>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={loading || !code}
            className={classes.button}
          >
            {loading ? (
              <CircularProgress size={25} thickness={2} />
            ) : (
              'Vahvista'
            )}
          </Button>
        </CardActions>
      </form>
    )
  }

  return (
    <form onSubmit={handleLogin}>
      <div className={classes.form}>
        <div className={classes.input}>
          <TextField
            label="Käyttäjänimi"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            autoFocus
            disabled={loading}
          />
        </div>
        <div className={classes.input}>
          <TextField
            label="Salasana"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            disabled={loading}
          />
        </div>
      </div>
      <CardActions>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          disabled={loading || !username || !password}
          className={classes.button}
        >
          {loading ? (
            <CircularProgress size={25} thickness={2} />
          ) : (
            'Kirjaudu'
          )}
        </Button>
      </CardActions>
    </form>
  )
}

export function LoginPage(props: any) {
  return (
    <Login {...props}>
      <CustomLoginForm />
    </Login>
  )
}
